import { CompanySettings, MarketingSiteSettings, UserProfile, Task, UpdateTaskInput } from '@repo/shared-types';
import { apiTransport as router } from './transport';
import { getContext } from './core';

export interface DashboardTrendMetric {
  value: number;
  trend: 'up' | 'down' | 'neutral';
}

export interface DashboardTrendsResponse {
  revenue: DashboardTrendMetric | null;
  occupancy: DashboardTrendMetric | null;
  openTickets: DashboardTrendMetric | null;
  completedTickets: DashboardTrendMetric | null;
  pendingApplications: DashboardTrendMetric | null;
  activeLeases: DashboardTrendMetric | null;
}

export const coreDomainsApi = {
  dashboard: {
    getTrends: async (): Promise<DashboardTrendsResponse> => router.get<DashboardTrendsResponse>('/dashboard/trends'),
  },
  settings: {
    get: async (): Promise<CompanySettings> => router.get<CompanySettings>('/settings'),
    update: async (updates: Partial<CompanySettings>): Promise<CompanySettings> => router.patch<CompanySettings>('/settings', updates)
  },
  marketing: {
    site: {
      get: async (): Promise<MarketingSiteSettings> => router.get<MarketingSiteSettings>('/settings/marketing'),
      update: async (updates: Partial<MarketingSiteSettings>): Promise<MarketingSiteSettings> => router.patch<MarketingSiteSettings>('/settings/marketing', updates)
    }
  },
  users: {
    get: async (id: string): Promise<UserProfile> => router.get<UserProfile>(`/users/${id}`),
    update: async (id: string, data: Partial<UserProfile>): Promise<UserProfile> => router.patch<UserProfile>(`/users/${id}`, data)
  },
  tasks: {
    list: async (assignee?: string) => router.get<Task[]>(`/tasks${assignee ? `?assignee=${assignee}` : ''}`),
    get: async (id: string) => router.get<Task>(`/tasks/${id}`),
    create: async (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
      const context = getContext();
      return router.post<Task>('/tasks', task, context);
    },
    update: async (id: string, updates: UpdateTaskInput) => {
      const context = getContext();
      return router.patch<Task>(`/tasks/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/tasks/${id}`, context);
    }
  },
  onboarding: {
    list: async () => router.get<unknown[]>('/onboarding/signups'),
    create: async (data: {
      role: string;
      stepData: Record<string, unknown>;
      email?: string;
      name?: string;
      source?: string;
      isSelfManagingOwner?: boolean;
      managementApproach?: 'self-manage-all' | 'pm-for-all' | 'mixed';
    }) => router.post<unknown>('/onboarding/signups', data),
    createInvite: async (data: { applicationId?: string; email?: string; propertyId?: string; unitId?: string }) => {
      const context = getContext();
      return router.post<{ inviteToken: string; inviteUrl: string; expiresAt: string }>('/onboarding/invites', data, context);
    },
    getInviteByToken: async (token: string) =>
      router.get<{ applicationId: string | null; email: string | null; propertyId: string | null; unitId: string | null } | null>(
        `/onboarding/invites/${encodeURIComponent(token)}`
      ),
  }
};

